import {
    CardCollection,
    DateIO,
    eachArray,
    eq,
    Instance,
    Knot,
    noop,
    Objekt,
    Promize,
    Query,
    Table,
} from '@siposdani87/sui-js';
import { app } from '../app';
import { AssetService } from '@services/assetService';
import { GoogleChartService } from '@services/googleChartService';
import { HuntService } from '@services/huntService';
import { IndividualHuntService } from '@services/individualHuntService';
import { LanguageService } from '@services/languageService';
import { OrganizationService } from '@services/organizationService';
import { SocialHuntService } from '@services/socialHuntService';
import { UserService } from '@services/userService';
import { UtilService } from '@services/utilService';
import { initCardCollection, initTable } from '@utils/collectionSetup';
import { DashboardController } from './dashboardController';

export class HuntsIndexByUserController extends DashboardController {
    static readonly inject = [
        'instances',
        'huntService',
        'socialHuntService',
        'individualHuntService',
        'googleChartService',
        'organizationService',
        'userService',
        'utilService',
        'assetService',
        'languageService',
    ] as const;

    userId!: string;
    huntCardCollection!: CardCollection;
    individualHuntsTable!: Table;

    constructor(
        instances: Instance,
        private huntService: HuntService,
        private socialHuntService: SocialHuntService,
        private individualHuntService: IndividualHuntService,
        private googleChartService: GoogleChartService,
        private organizationService: OrganizationService,
        private userService: UserService,
        private utilService: UtilService,
        private assetService: AssetService,
        private languageService: LanguageService,
    ) {
        super(instances);
    }

    protected override _initLayout(): void {
        this.userId = this.userService.getUser('id');

        this._initToolbarButtons();
        this._initFilterForm();
        this._initTabPanel('hunts');

        this._drawContent();
    }

    private _initToolbarButtons(): void {
        this.helper.iconButton('.btn-site-home', this.dom, () => {
            this.state.goBack('site.home');
        });
    }

    private _initHuntCardCollection(): void {
        this.huntCardCollection = initCardCollection(
            this.huntCardCollection,
            this.dom,
            '.hunt-collection',
            this.socialHuntService,
            {
                entityKey: 'social_hunts',
                fetchData: (params) =>
                    this.socialHuntService.getAllByUser(this.userId, params),
                extraParams: (params) => {
                    params.set('query', this.query);
                    params.set('organization_ids', this.organizationIds);
                    params.set(
                        'fields',
                        'id,title,start_at,location,photo,' +
                            this.organizationService.getAssociationTableFields(),
                    );
                },
            },
            this._huntCardKnot.bind(this),
            { formatStartAt: this.formatStartAt.bind(this) },
            { sort: { column: 'start_at', order: 'desc' } },
            'social',
            this.activeTab,
        )!;
    }

    private _initHuntTable(): void {
        this.individualHuntsTable = initTable(
            this.individualHuntsTable,
            this.dom,
            '.individual-hunts-table',
            this.individualHuntService,
            {
                entityKey: 'individual_hunts',
                fetchData: (params) =>
                    this.individualHuntService.getAllByUser(
                        this.userId,
                        params,
                    ),
                extraParams: (params) => {
                    params.set('query', this.query);
                    params.set('organization_ids', this.organizationIds);
                },
            },
            [
                {
                    style: (hunt) => {
                        return [
                            'info',
                            hunt.get<string>('description'),
                            false,
                            !hunt.get<string>('description'),
                        ];
                    },
                    click: noop(),
                },
            ],
            'individual',
            this.activeTab,
        )!;
    }

    protected override _drawContent(): Promize | boolean {
        this._initHuntChart();
        this._initHuntCardCollection();
        this._initHuntTable();
        return true;
    }

    private _initHuntChart(): void {
        if (eq(this.activeTab, 'hunts')) {
            this.googleChartService.loadSDK(['corechart', 'bar']).then(() => {
                const chartKnot = new Query('.chart', this.dom).getKnot();

                const chart = new google.visualization.ColumnChart(
                    chartKnot.getNode(),
                );
                const chartOptions: google.visualization.ColumnChartOptions = {
                    // width: '100%',
                    height: 460,
                    legend: { position: 'bottom' },
                    colors: ['#795548', '#FF9800'],
                    isStacked: true,
                };

                this.huntService
                    .getAllByUser(this.userId, {
                        fields: 'id,start_at,end_at,class_type',
                        column: 'start_at',
                        order: 'asc',
                        limit: 100,
                    })
                    .then((response) => {
                        const hunts = response.get<Objekt[]>('hunts');
                        const values: Record<string, number[]> = {};
                        let startYear: number | null = null;
                        let endYear: number | null = null;
                        let startWeek: number | null = null;
                        let endWeek: number | null = null;
                        eachArray(hunts, (hunt, _index) => {
                            const typeIndex =
                                hunt.get<string>('class_type') === 'individual_hunt'
                                    ? 0
                                    : 1;
                            const startAt = DateIO.parse(hunt.get<string>('start_at'));
                            const year = DateIO.getYear(startAt);
                            const week = DateIO.getWeek(startAt);
                            if (startYear === null || startYear > year) {
                                startYear = year;
                                startWeek = week;
                            }
                            if (endYear === null || endYear < year) {
                                endYear = year;
                                endWeek = week;
                            }
                            const key = this.languageService.format(
                                startAt,
                                'format.week',
                            );
                            if (!values[key]) {
                                values[key] = [0, 0];
                            }
                            values[key][typeIndex]++;
                        });

                        const rows: (string | number)[][] = [];
                        if (
                            startYear === null ||
                            endYear === null ||
                            startWeek === null ||
                            endWeek === null
                        ) {
                            return;
                        }
                        for (let i = startYear; i <= endYear; i++) {
                            const startJ =
                                i === startYear
                                    ? startWeek
                                    : DateIO.getISOWeek(
                                          DateIO.parse(
                                              '01-01-' + i,
                                              'MM-DD-YYYY',
                                          ),
                                      );
                            const endJ =
                                i === endYear
                                    ? endWeek
                                    : DateIO.getISOWeek(
                                          DateIO.parse(
                                              '12-30-' + i,
                                              'MM-DD-YYYY',
                                          ),
                                      );
                            for (let j = startJ; j <= endJ; j++) {
                                const key = this.languageService.format(
                                    DateIO.parse(`${i}-${j}`, 'YYYY-W'),
                                    'format.week',
                                );
                                if (!values[key]) {
                                    values[key] = [0, 0];
                                }
                                rows.push([key, ...values[key]]);
                            }
                        }

                        const data = new google.visualization.DataTable();
                        data.addColumn('string', '');
                        data.addColumn(
                            'number',
                            this.languageService.translate(
                                'captions.individual_hunts.my_index',
                            ),
                        );
                        data.addColumn(
                            'number',
                            this.languageService.translate(
                                'captions.social_hunts.my_index',
                            ),
                        );
                        eachArray(rows, (row) => {
                            data.addRow(row);
                        });
                        chart.draw(data, chartOptions);
                    });
            });
        }
    }

    private _huntCardKnot(cardKnot: Knot, hunt: Objekt): void {
        this._initHuntShowButton(cardKnot, hunt);
        this._initCardButtons(cardKnot, hunt);
        this.utilService.initOrganizationLink(cardKnot, hunt);
    }

    private _initHuntShowButton(cardKnot: Knot, hunt: Objekt): void {
        this.helper.link(
            'a.card',
            cardKnot,
            (href) => {
                this.state.go(href);
            },
            this.state.resolveUrl('hunts.show', {
                huntId: hunt.get<string>('id'),
            }),
        );
    }

    private _initCardButtons(cardKnot: Knot, hunt: Objekt): void {
        // let actionsKnot = new Query('.actions', cardKnot).getKnot();
    }

    formatStartAt(startAt: string): string {
        return this.languageService.format(
            DateIO.parse(startAt),
            'format.datetime',
        );
    }

    exit(): void {
        super.exit();

        this.googleChartService.reset();
    }
}

export const huntsIndexByUserController = app.controller(
    'huntsIndexByUserController',
    HuntsIndexByUserController,
);
